import { useEffect, useRef, useState } from "react";
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { FaLocationDot } from "react-icons/fa6";
import dayjs from "dayjs";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import TextFieldCustom from "../../../../component/elements/textfieldCustom";
import RadioCustom from "../../../../component/elements/radioCustom";
import CustomButton from "../../../../component/elements/CustomButton/CustomButton";
import DatePickerCustom from "../../../../component/elements/datePicker";
import ProfileImageSection from "../../../../component/profileImageSection";
import MapComponent from "../../../../component/mapComponent";
import DocumentModal from "../../../../component/documentModal/DocumentModal";
import BackButton from "../../../../component/backButton";

import { gender } from "../../../../constants/formConstants";
import { validateForm } from "../../../../utils/formValidation/validation";
import { personalInfoRules } from "../../../../utils/formValidation/validationRules";
import { getLatLngFromZip, reverseGeocode } from "../../../../utils/latLongFetch";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PrivacyPolicyContent from "../../../../constants/policyAndTerms/privacyPolicy";

// Reusable PDF Viewer Component
const PDFViewer = ({ pdfUrl, title = "Document" }) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="relative w-full h-[70vh] min-h-[500px] bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <CircularProgress size={40} sx={{ color: "#3b5998" }} />
        </div>
      )}
      <iframe
        src={`${pdfUrl}#toolbar=0&navpanes=0`}
        width="100%"
        height="100%"
        title={title}
        onLoad={() => setIsLoading(false)}
        className="border-none"
      >
        <p className="p-4 text-center">
          Your browser does not support PDFs. 
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1">
            Download the PDF to view it.
          </a>
        </p>
      </iframe>
    </div>
  );
};

export default function DetailsForm({
  email_id = "",
  phone_number = "",
  onContinue,
  userType,
}) {
  const { personalDetails } = useSelector((state) => state.counter);
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const [isMapOpen, setIsMapOpen] = useState(false);
  const formDataWhenMapOpenedRef = useRef({
    coordinates: null,
    addressLine1: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
  });
  const [openPrivacyModal, setOpenPrivacyModal] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    mobile: phone_number || "",
    email: email_id || "",
    birthDate: null,
    gender: "",
    addressLine1: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
    profileImage: null,
    coordinates: null,
    privacy_policy_accepted: false,
    terms_and_conditions_accepted: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const [resolvedLatLong, setResolvedLatLong] = useState(null);
  const [resolvingZip, setResolvingZip] = useState(false);

  useEffect(() => {
    if (personalDetails && Object.keys(personalDetails).length > 0) {
      setFormData(personalDetails);
    }
  }, [personalDetails]);

  useEffect(() => {
    const zip = (formData.zip || "").toString().trim();
    if (zip.length < 5) {
      setResolvedLatLong(null);
      return;
    }
    let cancelled = false;
    setResolvingZip(true);
    getLatLngFromZip(zip).then((loc) => {
      if (cancelled) return;
      setResolvingZip(false);
      setResolvedLatLong(loc ? { latitude: loc.latitude, longitude: loc.longitude } : null);
    }).catch(() => {
      if (!cancelled) {
        setResolvingZip(false);
        setResolvedLatLong(null);
      }
    });
    return () => { cancelled = true; };
  }, [formData.zip]);

  const hasValidLocation = formData.coordinates != null || resolvedLatLong != null;

  const updateField = (field) => (val) =>
    setFormData((prev) => ({ ...prev, [field]: val?.target?.value ?? val }));

  // Helper function to get correct PDF path based on userType
  const getTermsPdfUrl = () => {
    switch (userType) {
      case "business user"||"employee user":
        return "https://sembe-dev.s3.us-east-1.amazonaws.com/system_doc/Tydii+Community+Partner+TOS+1-3-26.pdf";
      case "delivery user":
        return "https://sembe-dev.s3.us-east-1.amazonaws.com/system_doc/driverConsent.pdf";
      default:
        return "https://sembe-dev.s3.us-east-1.amazonaws.com/system_doc/customerConsent.pdf";
    }
  };

  const handleSubmit = async () => {
    if (!acceptedPrivacy || !acceptedTerms) {
      showSnackbar(
        "Please accept Privacy Policy and Terms & Conditions",
        "error"
      );
      return;
    }

    const errors = validateForm(formData, personalInfoRules);
    setFormErrors(errors);

    if (Object.keys(errors).length) {
      showSnackbar("Please complete all required fields.", "error");
      return;
    }

    if (!hasValidLocation) {
      showSnackbar("Please pin your location on the map or enter a valid zip code.", "error");
      return;
    }

    let updated = { ...formData };
    if (formData.coordinates) {
      updated.latitude = formData.coordinates.lat;
      updated.longitude = formData.coordinates.lng;
    } else if (resolvedLatLong) {
      updated.latitude = resolvedLatLong.latitude;
      updated.longitude = resolvedLatLong.longitude;
    } else {
      const loc = await getLatLngFromZip(formData.zip);
      if (!loc) {
        showSnackbar("Failed to get location from zip code.", "error");
        return;
      }
      updated.latitude = loc.latitude;
      updated.longitude = loc.longitude;
    }

    onContinue(updated, "personal-info");
  };

  return (
    <div className="w-full h-[calc(100vh-85px)] overflow-y-auto">
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto bg-white rounded-2xl py-4">
        <div className="w-full max-w-4xl px-8 mt-3 flex items-center">
          <BackButton onClick={() => navigate("/auth/account")} />
        </div>

        <div className="text-2xl font-semibold">Register Your Account</div>
        <div className="text-sm mb-4">Enter your details to Register</div>

        <ProfileImageSection
          imageUrl={formData.profileImage}
          SaveProfileImage={(url) => updateField("profileImage")(url)}
        />

        <div className="w-full max-w-4xl px-8">
          <div className="text-sm font-semibold text-gray-700 mb-3">
            Basic Details
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <TextFieldCustom
              label="Full Name*"
              value={formData.fullName}
              onChange={updateField("fullName")}
              error={!!formErrors.fullName}
              helperText={formErrors.fullName || " "}
            />

            <div>
              <PhoneInput
                country={"us"}
                value={formData.mobile}
                onChange={(val) => updateField("mobile")(val)}
                inputClass="!w-full !h-[53px] !border !border-[#d1d5db] !rounded-[10px]"
                containerClass="w-full"
                disabled={!!phone_number}
              />
              {formErrors.mobile && (
                <div className="text-red-600 text-xs mt-1">
                  {formErrors.mobile}
                </div>
              )}
            </div>

            <TextFieldCustom
              label="Email*"
              disabled={!!email_id}
              value={formData.email}
              onChange={updateField("email")}
              error={!!formErrors.email}
              helperText={formErrors.email || " "}
            />

            <DatePickerCustom
              label="Birth Date*"
              value={formData.birthDate}
              onChange={updateField("birthDate")}
              maxDate={dayjs()}
              error={!!formErrors.birthDate}
              helperText={formErrors.birthDate || " "}
            />
          </div>

          <FormControl error={!!formErrors.gender}>
            <div className="font-medium text-gray-600">Gender</div>
            <RadioGroup
              row
              value={formData.gender}
              onChange={updateField("gender")}
            >
              {gender.map((g) => (
                <FormControlLabel
                  key={g}
                  value={g}
                  control={<RadioCustom />}
                  label={g[0].toUpperCase() + g.slice(1)}
                />
              ))}
            </RadioGroup>
            <FormHelperText>{formErrors.gender || " "}</FormHelperText>
          </FormControl>

          <div className="text-sm font-semibold text-gray-700 mb-2 mt-3">
            Address Details
          </div>

          <TextFieldCustom
            label="Address*"
            multiline
            rows={2}
            value={formData.addressLine1}
            onChange={updateField("addressLine1")}
            error={!!formErrors.addressLine1}
            helperText={formErrors.addressLine1 || " "}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {[
              { label: "Unit", key: "landmark" },
              { label: "City*", key: "city" },
              { label: "State*", key: "state" },
              { label: "Zip Code*", key: "zip" },
            ].map(({ label, key }) => (
              <TextFieldCustom
                key={key}
                label={label}
                value={formData[key]}
                onChange={updateField(key)}
                error={!!formErrors[key]}
                helperText={formErrors[key] || " "}
              />
            ))}
          </div>

          {!hasValidLocation && (
            <p className="text-amber-600 text-sm mt-1">
              Pin your location on the map or enter a valid zip code to continue.
            </p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <button
              type="button"
              onClick={() => {
                formDataWhenMapOpenedRef.current = {
                  coordinates: formData.coordinates,
                  addressLine1: formData.addressLine1 ?? "",
                  landmark: formData.landmark ?? "",
                  city: formData.city ?? "",
                  state: formData.state ?? "",
                  zip: formData.zip ?? "",
                };
                setIsMapOpen(true);
              }}
              className="text-[#3b5998] hover:underline inline-flex items-center gap-1 text-sm font-medium"
            >
              <FaLocationDot /> Pin your location on map
            </button>
            {formData.coordinates != null && (
              <span className="text-xs text-gray-500">
                Location set ({Number(formData.coordinates.lat).toFixed(5)}, {Number(formData.coordinates.lng).toFixed(5)})
              </span>
            )}
            {!formData.coordinates && formData.zip?.length >= 5 && (
              resolvingZip ? (
                <span className="text-xs text-gray-500">Resolving zip...</span>
              ) : resolvedLatLong ? (
                <span className="text-xs text-green-600">Zip verified</span>
              ) : (
                <span className="text-xs text-amber-600">Enter a valid zip code</span>
              )
            )}
          </div>

          {isMapOpen && (
            <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
              <div className="flex justify-between items-center px-2 py-1.5 bg-gray-50 border-b border-gray-200">
                <span className="text-sm text-gray-600">Click on the map to set pin, then confirm or close</span>
                <button
                  type="button"
                  onClick={() => {
                    const initial = formDataWhenMapOpenedRef.current;
                    setFormData((prev) => ({
                      ...prev,
                      coordinates: initial.coordinates,
                      addressLine1: initial.addressLine1,
                      landmark: initial.landmark,
                      city: initial.city,
                      state: initial.state,
                      zip: initial.zip,
                    }));
                    setIsMapOpen(false);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded"
                  aria-label="Close map"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <MapComponent
                isOpen={isMapOpen}
                address={[formData.addressLine1, formData.city, formData.state, formData.zip].filter(Boolean).join(", ")}
                onSelect={async (coords) => {
                  const address = await reverseGeocode(coords.lat, coords.lng);
                  setFormData((prev) => ({
                    ...prev,
                    coordinates: coords,
                    ...(address && {
                      addressLine1: address.addressLine1 ?? prev.addressLine1,
                      landmark: address.landmark ?? prev.landmark,
                      city: address.city ?? prev.city,
                      state: address.state ?? prev.state,
                      zip: address.zip ?? prev.zip,
                    }),
                  }));
                }}
                height="320px"
              />
              <div className="flex justify-end gap-2 p-2 bg-gray-50">
                <CustomButton
                  onClick={() => {
                    const initial = formDataWhenMapOpenedRef.current;
                    setFormData((prev) => ({
                      ...prev,
                      coordinates: initial.coordinates,
                      addressLine1: initial.addressLine1,
                      landmark: initial.landmark,
                      city: initial.city,
                      state: initial.state,
                      zip: initial.zip,
                    }));
                    setIsMapOpen(false);
                  }}
                  customeClass="primary-custom-button h-[36px] px-3"
                >
                  Close
                </CustomButton>
                <CustomButton
                  onClick={() => setIsMapOpen(false)}
                  customeClass="gradient-button h-[36px] px-3"
                >
                  Confirm location
                </CustomButton>
              </div>
            </div>
          )}

          {/* Privacy & Terms */}
          <div className="space-y-3 mt-5">
            <div className="flex gap-2">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAcceptedPrivacy(checked);
                  setFormData((prev) => ({
                    ...prev,
                    privacy_policy_accepted: checked,
                  }));
                }}
              />

              <span className="text-sm">
                I agree to the{" "}
                <button
                  className="text-blue-600 underline"
                  onClick={() => setOpenPrivacyModal(true)}
                >
                  Privacy Policy
                </button>
              </span>
            </div>

            <div className="flex gap-2">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAcceptedTerms(checked);
                  setFormData((prev) => ({
                    ...prev,
                    terms_and_conditions_accepted: checked,
                  }));
                }}
              />

              <span className="text-sm">
                I agree to the{" "}
                <button
                  className="text-blue-600 underline"
                  onClick={() => setOpenTermsModal(true)}
                >
                  Terms & Conditions
                </button>
              </span>
            </div>
          </div>

          <div className="flex justify-center pt-4 pb-6">
            <CustomButton
              onClick={handleSubmit}
              disabled={!(acceptedPrivacy && acceptedTerms && hasValidLocation)}
              customeClass={`auth-button w-[300px] h-[45px] ${!(acceptedPrivacy && acceptedTerms && hasValidLocation)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
                }`}
            >
              Continue
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <DocumentModal
        open={openPrivacyModal}
        onClose={() => setOpenPrivacyModal(false)}
        title="Privacy Policy"
      >
        {PrivacyPolicyContent}
      </DocumentModal>

      {/* Updated Terms & Conditions Modal with PDF Viewer */}
      <DocumentModal
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        title="Terms & Conditions"
      >
        <PDFViewer 
          pdfUrl={getTermsPdfUrl()} 
          title="Terms & Conditions" 
        />
      </DocumentModal>
    </div>
  );
}
